import {
    DeleteTwoTone,
    EditTwoTone,
    EyeTwoTone,
    MinusCircleOutlined,
    PlusCircleTwoTone,
    PlusOutlined,
    ProfileTwoTone,
    QuestionCircleOutlined,
    UploadOutlined,
} from "@ant-design/icons";

import {
    Button,
    Card,
    Checkbox,
    Col,
    Collapse,
    DatePicker,
    Drawer,
    Form,
    Input,
    InputNumber,
    Modal,
    notification,
    Popconfirm,
    Popover,
    Row,
    Select,
    Skeleton,
    Typography,
    Tooltip,
} from "antd";
import Upload from "antd/lib/upload/Upload";
import moment from "moment";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import {
    decrypt,
    encrypt,
    formatDate,
    getBase64,
    getUserRole,
    weekDays,
} from "../../services/util";
import CustomHeader from "../common/Header";
import CustomTable from "../common/Table";
import { getUserNamePasswordFormList } from "./FormFields";
import "./Table.css";
import { AddHotel } from "./AddHotel";

const { Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const emptyFormValues = {
    property_name: "",
    live_date: "",
    property_address: "",
    property_city: "",
    property_state: "",
    property_zip_code: "",
    brand: "",
    ship_date: "",
    lock_provider: "",
    property_key: "",
    property_key_software: "",
    pbx: "",
    pms: "",
    gen_kiosk: "",
    credit_terminal: "",
    kiosk_serial: "",
    internet_service: "",
    notes: "",
    kiosk_id: "",
    him_version: "",
    kiosk_version: "",
    tti_version: "",
    desktop_name: "",
    anydesk: "",
    ip_address: "",
    pbx_contact: "",
    lock_contact: "",
    pms_contact: "",
    pms_url: "",
    pms_user_id: "",
    lock_user_id: "",
    email: "",
    confluence_url: "",
    support_user: "",
    hotel_id: "",
    kiosk_number: "",
    module_service_version_number: "",
    health_monitor_version_number: "",
    ftp_service_version_number: "",
    bill_acceptor_port_number: "",
    bill_dispenser_port_number: "",
    printer_port_number: "",
    key_dispenser_port_number: "",
};

function KioskPropertiesTable() {
    const [form] = Form.useForm();
    const userRole = getUserRole();
    const staff_id = localStorage.getItem(`x-dashboard-staffid`);
    const [tableData, setTableData] = useState([]);
    const [searchKey, setSearchKey] = useState("");
    const [historyTableData, setHistoryTableData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalHistoryRecords, setTotalHistoryRecords] = useState(0);
    const [visible, setVisible] = useState(false);
    const [columnSelectionVisible, setColumnSelectionVisible] = useState(false);
    const [historyTableVisible, setHistoryTableVisible] = useState(false);
    const [viewVisible, setViewVisible] = useState(false);
    const [viewData, setViewData] = useState([]);
    const [title, setTitle] = useState("Edit");
    const [selectedTitle, setSelectedTitle] = useState([]);
    const [buttonStatus, setButtonStatus] = useState(false);
    const [authenticationModalVisible, setAuthenticationModalVisible] =
        useState(false);
    const [authenticationForm] = Form.useForm();
    const [importModalVisible, setImportModalVisible] = useState(false);
    const [importData, setImportData] = useState([]);
    const [historyFormData, setHistoryFormData] = useState({
        staff_id,
        limit: 5,
        page: 0,
    });

    const [shift_timings, setShiftTimings] = useState([]);
    const [shiftDays, setShiftDays] = useState([]);
    const [shiftVisible, setShiftVisible] = useState(false);
    const [formData, setFormData] = useState({
        staff_id,
        page: 0,
        limit: 10,
    });
    const [addHotelForKioskId, setAddHotelForKioskId] = useState(0);

    const {
        status: kioskPropertyStatus,
        data: kioskPropertyRes,
        refetch: fetchKioskProperty,
    } = useQuery(
        "kioskProperties",
        async () => {
            return await HTTP("/kioskProperties/getKioskProperty", {
                ...formData,
            });
        },
        { enabled: false },
    );

    useEffect(() => {
        fetchKioskProperty();
    }, []);

    useEffect(() => {
        fetchKioskProperty();
    }, [formData]);

    useEffect(() => {
        if (kioskPropertyStatus === "success") {
            const { data: kioskData, total } = kioskPropertyRes;
            setTotalRecords(total);
            setTableData(kioskData);
        }
    }, [kioskPropertyStatus, kioskPropertyRes]);

    function handleTableChange(pagination, _, sorter) {
        const { current, pageSize } = pagination;
        const { order, field } = sorter;
        const others = {
            page: current - 1,
            limit: pageSize,
        };
        if (order !== undefined) {
            others.sorting = order === "ascend" ? 1 : 2;
            others.sort_by = field;
        } else {
            delete formData.sorting;
            delete formData.sort_by;
        }
        const updatedFormData = { ...formData, ...others };
        setFormData(updatedFormData);
    }

    async function handleHistoryTableChange(pagination, _, sorter) {
        const { current, pageSize } = pagination;
        const { order, field } = sorter;
        const others = {
            page: current - 1,
            limit: pageSize,
        };
        if (order !== undefined) {
            others.sorting = order === "ascend" ? 1 : 2;
            others.sort_by = field;
        } else {
            delete formData.sorting;
            delete formData.sort_by;
        }
        const updatedFormData = { ...historyFormData, ...formData, ...others };
        setHistoryFormData(updatedFormData);
        const { success, data, message, total } = await HTTP(
            "/kioskProperties/getKioskPropertyHistory",
            { ...updatedFormData },
        );
        if (success) {
            setHistoryTableData(data);
            setTotalHistoryRecords(total);
        } else {
            notification["error"]({
                message: message,
            });
        }
    }

    const columns = [
        {
            title: "Property Name",
            dataIndex: "property_name",
            key: "property_name",
            sorter: () => {},
            header: "Property Info",
            required: true,
        },
        {
            title: "Brand",
            dataIndex: "brand",
            key: "brand",
            header: "Property Info",
            sorter: () => {},
        },
        {
            title: "Street",
            dataIndex: "property_address",
            key: "property_address",
            header: "Property Info",
            sorter: () => {},
        },
        {
            title: "City",
            dataIndex: "property_city",
            key: "property_city",
            header: "Property Info",
            sorter: () => {},
        },
        {
            title: "State",
            dataIndex: "property_state",
            key: "property_state",
            header: "Property Info",
            sorter: () => {},
        },
        {
            title: "Zipcode",
            dataIndex: "property_zip_code",
            key: "property_zip_code",
            header: "Property Info",
            sorter: () => {},
        },
        {
            title: "Live Date",
            dataIndex: "live_date",
            key: "live_date",
            header: "Property Info",
            sorter: () => {},
        },
        {
            title: "Ship Date",
            dataIndex: "ship_date",
            key: "ship_date",
            header: "Property Info",
            sorter: () => {},
        },
        {
            title: "Lock Provider",
            dataIndex: "lock_provider",
            key: "lock_provider",
            sorter: () => {},
            required: true,
        },
        {
            title: "Property Key",
            dataIndex: "property_key",
            key: "property_key",
            sorter: () => {},
        },
        {
            title: "Property Key Software",
            dataIndex: "property_key_software",
            key: "property_key_software",
            sorter: () => {},
        },
        {
            title: "PBX",
            dataIndex: "pbx",
            key: "pbx",
            sorter: () => {},
            required: true,
        },
        {
            title: "PMS",
            dataIndex: "pms",
            key: "pms",
            sorter: () => {},
            required: true,
        },
        {
            title: "Generation Of Kiosk",
            dataIndex: "gen_kiosk",
            key: "gen_kiosk",
            sorter: () => {},
        },
        {
            title: "No. Of Kiosk",
            dataIndex: "number_of_kiosk",
            key: "number_of_kiosk",
            sorter: () => {},
        },
        {
            title: "No. Of Agents",
            dataIndex: "kiosk_frontdesk_agents",
            key: "kiosk_frontdesk_agents",
            sorter: () => {},
        },
        {
            title: "Credit Terminal",
            dataIndex: "credit_terminal",
            key: "credit_terminal",
            sorter: () => {},
        },
        {
            title: "Shift Settings",
            dataIndex: "shift_settings",
            key: "shift_settings",
            render: (settings, record) => {
                return weekDays.map((e) => (
                    <>
                        <span key={`${record.hotel_id}_${e.title}`}>{`${
                            e.title
                        }: ${
                            settings[e.key] !== undefined &&
                            settings[e.key]?.length > 0
                                ? settings[e.key].map((shift) => {
                                      const time = record.shift_timings.find(
                                          ({ type }) => type == shift,
                                      );
                                      return `${shift} (${formatDate(
                                          time.start_time,
                                          "HH:mm",
                                      )} - ${formatDate(
                                          time.end_time,
                                          "HH:mm",
                                      )})`;
                                  })
                                : "NA"
                        }`}</span>
                        <br />
                    </>
                ));
            },
            sorter: () => {},
        },
        {
            title: "Kiosk Serial",
            dataIndex: "kiosk_serial",
            key: "kiosk_serial",
            sorter: () => {},
        },
        {
            title: "Internet Provider",
            dataIndex: "internet_service",
            key: "internet_service",
            sorter: () => {},
        },
        {
            title: "Notes",
            dataIndex: "notes",
            key: "notes",
            render: (text, record) => (
                <Tooltip placement="rightTop" title={text}>
                    <span>
                        {text.length <= 30
                            ? text
                            : `${text.substring(0, 30)}...`}
                    </span>
                </Tooltip>
            ),
            sorter: () => {},
        },
        {
            title: "Kiosk Id",
            dataIndex: "kiosk_id",
            key: "kiosk_id",
            sorter: () => {},
        },
        {
            title: "Him Version",
            dataIndex: "him_version",
            key: "him_version",
            sorter: () => {},
        },
        {
            title: "TTI Version",
            dataIndex: "tti_version",
            key: "tti_version",
            sorter: () => {},
        },
        {
            title: "Kiosk Version",
            dataIndex: "kiosk_version",
            key: "kiosk_version",
            render: (text, record) => (
                <>
                    <span className="margin-right-10">
                        {text && text != "" ? text : "-"}
                    </span>
                    <Button
                        onClick={() =>
                            getKioskVersion({ property_id: record.id })
                        }
                        type="primary"
                        size="small"
                    >
                        Get Updated Version
                    </Button>
                </>
            ),
        },
        {
            title: "Desktop Name",
            dataIndex: "desktop_name",
            key: "desktop_name",
            sorter: () => {},
        },
        {
            title: "Anydesk",
            dataIndex: "anydesk",
            key: "anydesk",
            sorter: () => {},
            width: "10%",
            required: true,
        },
        {
            title: "Ip",
            dataIndex: "ip_address",
            key: "ip_address",
            sorter: () => {},
        },
        {
            title: "PBX Contact",
            dataIndex: "pbx_contact",
            key: "pbx_contact",
            sorter: () => {},
        },
        {
            title: "Lock Contact",
            dataIndex: "lock_contact",
            key: "lock_contact",
            sorter: () => {},
        },
        {
            title: "PMS Contact",
            dataIndex: "pms_contact",
            key: "pms_contact",
            sorter: () => {},
        },
        {
            title: "PMS Url",
            dataIndex: "pms_url",
            key: "pms_url",
            sorter: () => {},
        },
        {
            title: "PMS User Id",
            dataIndex: "pms_settings",
            key: "pms_settings",
            render: (text) => {
                return (
                    text?.length > 0 &&
                    text.map(
                        (e, index) =>
                            `${e.email}${index != text.length - 1 ? "," : ""}`,
                    )
                );
            },
            sorter: () => {},
        },
        {
            title: "PMS Password",
            render: (text, record) => (
                <Button
                    size="small"
                    type="primary"
                    id={record.id}
                    passwordtype="pms_settings"
                    onClick={handleGetPassword}
                >
                    PMS Password
                </Button>
            ),
            width: "10%",
        },
        {
            title: "Lock User Id",
            dataIndex: "lock_settings",
            key: "lock_settings",
            render: (text) => {
                return (
                    text?.length > 0 &&
                    text.map(
                        (e, index) =>
                            `${e.email}${index != text.length - 1 ? "," : ""}`,
                    )
                );
            },
            sorter: () => {},
        },
        {
            title: "Lock Password",
            render: (text, record) => (
                <Button
                    size="small"
                    type="primary"
                    id={record.id}
                    passwordtype="lock_settings"
                    onClick={handleGetPassword}
                >
                    Lock Password
                </Button>
            ),
            width: "10%",
        },
        {
            title: "Email",
            dataIndex: "general_settings",
            key: "general_settings",
            render: (text) => {
                return (
                    text?.length > 0 &&
                    text.map(
                        (e, index) =>
                            `${e.email}${index != text.length - 1 ? "," : ""}`,
                    )
                );
            },
            sorter: () => {},
        },
        {
            title: "Password",
            render: (text, record) => (
                <Button
                    size="small"
                    type="primary"
                    id={record.id}
                    passwordtype="general_settings"
                    onClick={handleGetPassword}
                >
                    Password
                </Button>
            ),
            width: "10%",
        },
        {
            title: "Confluence URL",
            dataIndex: "confluence_url",
            key: "confluence_url",
            sorter: () => {},
        },
        {
            title: "Aavgo support user",
            dataIndex: "support_user",
            key: "support_user",
            sorter: () => {},
        },
        {
            title: "Hotel ID",
            dataIndex: "hotel_id",
            key: "hotel_id",
            sorter: () => {},
        },
        {
            title: "Kiosk Number",
            dataIndex: "kiosk_number",
            key: "kiosk_number",
            sorter: () => {},
        },
        {
            title: "Aavgo Module Service Version Number",
            dataIndex: "module_service_version_number",
            key: "module_service_version_number",
            sorter: () => {},
        },
        {
            title: "Aavgo Service Health Monitor Version Number",
            dataIndex: "health_monitor_version_number",
            key: "health_monitor_version_number",
            sorter: () => {},
        },
        {
            title: "Aavgo FTP Service Version Number",
            dataIndex: "ftp_service_version_number",
            key: "ftp_service_version_number",
            sorter: () => {},
        },
        {
            title: "Bill Acceptor Port Number",
            dataIndex: "bill_acceptor_port_number",
            key: "bill_acceptor_port_number",
            sorter: () => {},
        },
        {
            title: "Bill Dispenser Port Number",
            dataIndex: "bill_dispenser_port_number",
            key: "bill_dispenser_port_number",
            sorter: () => {},
        },
        {
            title: "Printer Port Number",
            dataIndex: "printer_port_number",
            key: "printer_port_number",
            sorter: () => {},
        },
        {
            title: "Key Dispenser Port Number",
            dataIndex: "key_dispenser_port_number",
            key: "key_dispenser_port_number",
            sorter: () => {},
        },
        {
            title: "Kiosk Property Notes",
            dataIndex: "kiosk_property_notes",
            key: "kiosk_property_notes",
            render: (text, record) => (
                <Tooltip placement="rightTop" title={text}>
                    <span>
                        {text.length <= 30
                            ? text
                            : `${text.substring(0, 30)}...`}
                    </span>
                </Tooltip>
            ),
            sorter: () => {},
        },
        {
            title: "Action",
            fixed: "right",
            required: true,
            width: "8%",
            render: (_, record) => (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        gap: "3px",
                    }}
                >
                    <EyeTwoTone
                        className="actionIcon"
                        data-record={JSON.stringify(record)}
                        onClick={showDetails}
                    />
                    <Popconfirm
                        title="Are you sure want to delete?"
                        onConfirm={() => deleteKioskProperty(record.id)}
                        icon={
                            <QuestionCircleOutlined
                                style={{
                                    color: "red",
                                }}
                            />
                        }
                    >
                        <DeleteTwoTone className="actionIcon" />
                    </Popconfirm>
                    <EditTwoTone
                        className="actionIcon"
                        details={JSON.stringify(record)}
                        onClick={editKioskProperty}
                    />
                    <ProfileTwoTone
                        className="actionIcon"
                        id={record.id}
                        onClick={showHistory}
                    />
                    {record?.hotel_id == 0 &&
                        record?.hotel_created == false &&
                        userRole == "Super Admin" && (
                            <PlusCircleTwoTone
                                className="actionIcon"
                                id={record.id}
                                onClick={(e) => showAddHotelForm(record.id)}
                            />
                        )}
                </div>
            ),
        },
    ];

    function showAddHotelForm(id) {
        setAddHotelForKioskId(id);
    }

    function closeAddHotelForm(reload) {
        setAddHotelForKioskId(0);
        if (reload) {
            fetchKioskProperty();
        }
    }

    const getKioskVersion = async (formData) => {
        await HTTP("/kioskProperties/getKioskVersion", {
            staff_id,
            ...formData,
        });
        fetchKioskProperty();
    };

    const viewColumns = [
        {
            Header: "Property Info",
            keys: columns.filter((c) => c?.header == "Property Info"),
        },
        {
            Header: "Kiosk Details",
            keys: columns.filter(
                (c) =>
                    c?.header != "Property Info" &&
                    // also filter sensetive info like passwords and action
                    c.title !== "Password" &&
                    c.title !== "Lock Password" &&
                    c.title !== "PMS Password" &&
                    c.title !== "Action",
            ),
        },
    ];
    const [selectedColumns, setSelectedColumns] = useState(
        columns.reduce((prev, cur) => {
            if (cur?.required) prev.push(cur);
            return prev;
        }, []),
    );
    const [importColumns, setImportColumns] = useState(
        selectedColumns.reduce((prev, cur) => {
            if (cur.title !== "Action") {
                if (
                    cur.title === "PMS Password" ||
                    cur.title === "Lock Password" ||
                    cur.title === "Password"
                ) {
                    const recordKey =
                        cur.title === "Password"
                            ? "password"
                            : cur.title === "PMS Password"
                            ? "pms_password"
                            : "lock_password";
                    cur = {
                        ...cur,
                        render: (_, record) => <span>{record[recordKey]}</span>,
                    };
                }
                prev.push(cur);
            }

            return prev;
        }, []),
    );

    function handleGetPassword(e) {
        if (
            userRole == "Super Admin" ||
            userRole == "Admin" ||
            userRole == "Customer Success"
        ) {
            authenticationForm.setFieldsValue({
                property_id: e.currentTarget.getAttribute("id"),
                field: e.currentTarget.getAttribute("passwordtype"),
            });
            setAuthenticationModalVisible(true);
        } else {
            return notification["error"]({
                message: "You are not allowed to perform this action.",
            });
        }
    }

    async function showHistory(e) {
        const newFormData = {
            ...historyFormData,
            property_id: e.currentTarget.getAttribute("id"),
        };
        setHistoryFormData(newFormData);
        const { success, data, message, total } = await HTTP(
            "/kioskProperties/getKioskPropertyHistory",
            newFormData,
        );

        if (success) {
            setHistoryTableData(data);
            setHistoryTableVisible(true);
            setTotalHistoryRecords(total);
        } else {
            notification["error"]({
                message: message,
            });
        }
    }

    function showDetails(e) {
        const details = JSON.parse(e.currentTarget.getAttribute("data-record"));
        details.pms_settings = !Array.isArray(details.pms_settings)
            ? []
            : details.pms_settings;
        details.lock_settings = !Array.isArray(details.lock_settings)
            ? []
            : details.lock_settings;
        details.lock_settings = !Array.isArray(details.lock_settings)
            ? []
            : details.lock_settings;

        setViewData(details);
        setViewVisible(true);
    }

    function editKioskProperty(e) {
        if (
            !(
                userRole === "Super Admin" ||
                userRole === "Admin" ||
                userRole === "Customer Success" ||
                userRole === "Support"
            )
        ) {
            return notification["error"]({
                message: "You are not allowed to perform this action.",
            });
        }
        const details = JSON.parse(e.currentTarget.getAttribute("details"));
        const formValues = {
            ...emptyFormValues,
            ...details,
        };

        if (moment(details.live_date)._isValid) {
            formValues.live_date = moment(details.live_date);
        } else {
            delete formValues.live_date;
        }
        if (moment(details.ship_date)._isValid) {
            formValues.ship_date = moment(details.ship_date);
        } else {
            delete formValues.ship_date;
        }

        if (formValues.shift_settings.length > 0) {
            setShiftDays(
                details.shift_settings.reduce((prev, cur, currentIndex) => {
                    if (cur.length > 0) {
                        formValues[`shifts_${currentIndex}`] = cur;
                        prev.push(currentIndex);
                    }
                    return prev;
                }, []),
            );
        }

        if (formValues.shift_timings.length > 0) {
            formValues.shift_timings = formValues.shift_timings.map((t) => {
                t.start_time = moment(t.start_time);
                t.end_time = moment(t.end_time);
                return t;
            });
        }

        formValues.pms_settings =
            formValues?.pms_settings && Array.isArray(formValues.pms_settings)
                ? formValues.pms_settings
                : [];

        formValues.lock_settings =
            formValues?.lock_settings && Array.isArray(formValues.lock_settings)
                ? formValues.lock_settings
                : [];
        formValues.general_settings =
            formValues?.general_settings &&
            Array.isArray(formValues.general_settings)
                ? formValues.general_settings
                : [];
        setShiftTimings(formValues.shift_timings);
        form.setFieldsValue({
            ...formValues,
            ...(formValues.pms_settings?.length > 0
                ? {
                      pms_settings: formValues.pms_settings.map((e) => {
                          return { ...e, password: decrypt(e.password) };
                      }),
                  }
                : []),
            ...(formValues.lock_settings?.length > 0
                ? {
                      lock_settings: formValues.lock_settings.map((e) => {
                          return { ...e, password: decrypt(e.password) };
                      }),
                  }
                : []),
            ...(formValues.general_settings?.length > 0
                ? {
                      general_settings: formValues.general_settings.map((e) => {
                          return { ...e, password: decrypt(e.password) };
                      }),
                  }
                : []),
        });
        setTitle("Edit");
        setVisible(true);
    }

    async function deleteKioskProperty(id) {
        const { success, message } = await HTTP(
            "/kioskProperties/deleteKioskProperty",
            {
                staff_id,
                id,
            },
        );
        let type = "info";
        if (success === 0) {
            type = "error";
        }
        notification[type]({
            message: message,
        });
        fetchKioskProperty();
    }

    function closeModel() {
        setVisible(false);
        setShiftDays([]);
        setShiftTimings([]);
        closeShiftDrawer();
        form.resetFields();
    }

    async function handleSubmit(values) {
        values.shift_settings = weekDays.map((cur) => {
            return values[`shifts_${cur.key}`] ?? [];
        });

        let url = "/kioskProperties/addKioskProperty";
        if (values.id) {
            url = "/kioskProperties/editKioskProperty";
        }
        const formData = {
            ...values,
            staff_id,
        };
        formData.pms_settings =
            formData?.pms_settings?.length > 0
                ? formData.pms_settings.map((e) => {
                      return { ...e, password: encrypt(e.password) };
                  })
                : [];
        formData.lock_settings =
            formData?.lock_settings?.length > 0
                ? formData.lock_settings.map((e) => {
                      return { ...e, password: encrypt(e.password) };
                  })
                : [];
        formData.general_settings =
            formData?.general_settings?.length > 0
                ? formData.general_settings.map((e) => {
                      return { ...e, password: encrypt(e.password) };
                  })
                : [];
        const { success, message } = await HTTP(url, formData);
        closeModel();
        let type = "info";
        if (success === 0) {
            type = "error";
        }
        notification[type]({
            message: message,
        });
        fetchKioskProperty();
    }

    useEffect(() => {
        const selection = columns.reduce((prev, cur) => {
            if (cur?.required) prev.push(cur.title);
            return prev;
        }, []);

        setSelectedTitle(selection);
    }, []);

    function openAddModal() {
        if (
            !(
                userRole === "Super Admin" ||
                userRole === "Admin" ||
                userRole === "Customer Success" ||
                userRole === "Support"
            )
        ) {
            return notification["error"]({
                message: "You are not allowed to perform this action.",
            });
        }
        form.resetFields();
        setTitle("ADD");
        setVisible(true);
    }

    function handleColumnsSelectionVisible(visible) {
        setColumnSelectionVisible(visible);
        if (!visible) {
            const selected = columns.reduce((prev, cur) => {
                if (cur?.title && selectedTitle.includes(cur?.title)) {
                    prev.push(cur);
                }
                return prev;
            }, []);
            setSelectedColumns(selected);
            setImportColumns(
                selected.reduce((prev, cur) => {
                    if (cur.title !== "Action") {
                        if (
                            cur.title === "PMS Password" ||
                            cur.title === "Lock Password" ||
                            cur.title === "Password"
                        ) {
                            const recordKey =
                                cur.title === "Password"
                                    ? "password"
                                    : cur.title === "PMS Password"
                                    ? "pms_password"
                                    : "lock_password";
                            cur = {
                                ...cur,
                                render: (_, record) => (
                                    <span>{record[recordKey]}</span>
                                ),
                            };
                        }
                        prev.push(cur);
                    }

                    return prev;
                }, []),
            );
        }
    }

    function handleColumnSelction(selected) {
        setSelectedTitle(selected);
    }

    function getColumnsForm() {
        return (
            <Checkbox.Group
                onChange={handleColumnSelction}
                defaultValue={columns.reduce((prev, cur) => {
                    if (cur?.required) prev.push(cur.title);
                    return prev;
                }, [])}
            >
                {columns.map((cur) => {
                    return (
                        <>
                            <Checkbox
                                key={cur.title}
                                value={cur.title}
                                disabled={Boolean(cur.required)}
                            >
                                {cur.title}
                            </Checkbox>
                            <br />
                        </>
                    );
                }, [])}
            </Checkbox.Group>
        );
    }

    let historyColumns = [...selectedColumns];
    historyColumns = historyColumns.reduce((prev, cur) => {
        if (
            cur.title !== "PMS Password" &&
            cur.title !== "Lock Password" &&
            cur.title !== "Password" &&
            cur.title !== "Action"
        ) {
            prev.push(cur);
        }
        return prev;
    }, []);

    historyColumns.push({
        title: "Changed By",
        render: (text, record) => (
            <p>{`${record.changes_by_staff_first_name} ${record.changes_by_staff_last_name}`}</p>
        ),
    });

    function closeHistoryModel() {
        setHistoryTableVisible(false);
        setHistoryTableData([]);
    }

    function closeViewModel() {
        setViewVisible(false);
        setViewData([]);
    }

    function handleFileChange(info) {
        if (info.file.status === "error") {
            getBase64(info.file.originFileObj, async (imageUrl) => {
                const formData = {
                    staff_id,
                    file_name: info.file.name,
                    file_path: imageUrl,
                };
                const { success, data, message } = await HTTP(
                    "/kioskProperties/addBulkKioskFile",
                    {
                        ...formData,
                    },
                );
                if (!success) {
                    notification["error"]({
                        message,
                    });
                } else {
                    setImportModalVisible(true);
                    setImportData(data);
                }
            });
        }
    }

    function closeAuthenticationModal() {
        setAuthenticationModalVisible(false);
    }

    async function handleAuthenticationSubmit(values) {
        const formData = { staff_id, ...values };
        formData.password = encrypt(formData.password);
        setButtonStatus(true);
        const { success, data, message } = await HTTP(
            "/kioskProperties/getKioskPassword",
            formData,
        );
        setButtonStatus(false);
        closeAuthenticationModal();
        if (!success) {
            return notification["error"]({
                message,
            });
        } else {
            const columnTitle =
                values.field === "general_settings"
                    ? "Password"
                    : values.field === "pms_settings"
                    ? "PMS Password"
                    : "Lock Password";
            const index = selectedColumns.findIndex(
                (e) => e.title == columnTitle,
            );
            let newColumn = [...selectedColumns];
            newColumn.splice(index, 1, {
                ...selectedColumns[index],
                render: (text, record) => {
                    if (record.id == values.property_id) {
                        return (
                            <>
                                {data.password.map(({ email, password }, i) => (
                                    <div key={email}>
                                        {`${email} :${decrypt(password)}`},
                                    </div>
                                ))}
                            </>
                        );
                    } else {
                        return (
                            <Button
                                size="small"
                                type="primary"
                                id={record.id}
                                passwordtype={values.field}
                                onClick={handleGetPassword}
                            >
                                {selectedColumns[index].title}
                            </Button>
                        );
                    }
                },
            });
            setSelectedColumns(newColumn);
            setTimeout(() => {
                let updateDefaultColumn = [...newColumn];
                updateDefaultColumn.splice(index, 1, {
                    ...selectedColumns[index],
                    render: (text, record) => {
                        return (
                            <Button
                                size="small"
                                type="primary"
                                id={record.id}
                                passwordtype={values.field}
                                onClick={handleGetPassword}
                            >
                                {selectedColumns[index].title}
                            </Button>
                        );
                    },
                });
                setSelectedColumns(updateDefaultColumn);
            }, 5000);
        }
    }

    function setShiftDaysCheckbox(e) {
        let tempShiftDays = [...shiftDays];
        if (e.target.checked) {
            tempShiftDays.push(e.target.value);
            setShiftDays(tempShiftDays);
        } else {
            setShiftDays(tempShiftDays.filter((i) => i !== e.target.value));
        }
    }

    async function confirmImport() {
        const { success, data, message } = await HTTP(
            "/kioskProperties/addBulkKioskProperty",
            {
                staff_id,
                properties: importData,
            },
        );
        let notificationMessage = message;
        if (!success) {
            notificationMessage = `${
                importData.length - data.length
            } Updated Successfully. ${
                data.length
            } failed due to invalid data format.`;
        }
        notification["info"]({
            message: notificationMessage,
        });
        fetchKioskProperty();
        closeImportModel();
    }

    function closeImportModel() {
        setImportModalVisible(false);
        setImportData([]);
    }

    function handleSearchInputChange(event) {
        setSearchKey(event.target.value);
    }

    function handleSearchInputSubmit() {
        const updatedFormData = { ...formData, search_by: searchKey };
        setFormData(updatedFormData);
    }

    function OpenShiftDrawer() {
        setShiftVisible(true);
    }

    function closeShiftDrawer() {
        // shiftTimingsForm.resetFields();
        setShiftVisible(false);
    }

    const DateWiseshift = ({ weekDay, shiftSelected }) => {
        return (
            <>
                {shiftDays.includes(weekDay) && (
                    <>
                        <Form.Item name={`shifts_${weekDay}`}>
                            <Select size="small" mode="multiple">
                                {shift_timings.map((s) => (
                                    <Option key={s.type} value={s.type}>
                                        {s.type}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </>
                )}
            </>
        );
    };

    return (
        <>
            <Card className="dashboard-container">
                <CustomHeader
                    extraAfter={
                        (userRole === "Super Admin" ||
                            userRole === "Admin" ||
                            userRole === "Customer Success" ||
                            userRole === "Support") && (
                            <Button
                                type="primary"
                                style={{ marginRight: "5px" }}
                                onClick={openAddModal}
                            >
                                <PlusOutlined /> Add
                            </Button>
                        )
                    }
                />
                <Card
                    className="box-shadow margin-10"
                    title={
                        <>
                            <Input
                                placeholder="Property Name"
                                style={{ width: "20%" }}
                                className="margin-right-10"
                                value={searchKey}
                                onChange={handleSearchInputChange}
                            />
                            <Button
                                type="primary"
                                onClick={handleSearchInputSubmit}
                            >
                                Search
                            </Button>
                            <div className="right-header-elements ">
                                {(userRole === "Super Admin" ||
                                    userRole === "Admin" ||
                                    userRole === "Support") && (
                                    <Upload
                                        accept=".csv , .xlsx"
                                        onChange={handleFileChange}
                                        showUploadList={false}
                                    >
                                        <Button
                                            className="margin-right-10"
                                            type="primary"
                                            icon={<UploadOutlined />}
                                        >
                                            Import
                                        </Button>
                                    </Upload>
                                )}
                                <Popover
                                    placement="leftTop"
                                    content={getColumnsForm()}
                                    title="Select Columns"
                                    trigger="click"
                                    open={columnSelectionVisible}
                                    onOpenChange={handleColumnsSelectionVisible}
                                >
                                    <Button type="primary">Columns</Button>
                                </Popover>
                            </div>
                        </>
                    }
                >
                    {kioskPropertyStatus === "loading" ||
                    kioskPropertyStatus === "idle" ? (
                        <Skeleton
                            active
                            title={false}
                            paragraph={{ rows: 20, width: 40 }}
                        />
                    ) : (
                        <CustomTable
                            columns={selectedColumns}
                            tableData={tableData}
                            noOfrecords={totalRecords}
                            handleTableChange={handleTableChange}
                            rowKey="id"
                            className="kiosk-property-table"
                            isScroll
                        />
                    )}
                </Card>
            </Card>
            <Modal
                title={
                    <Row>
                        <Col span="18">
                            <span>{title}</span>
                        </Col>
                        <Col span="6">
                            <Button
                                type="primary"
                                size="small"
                                onClick={() => OpenShiftDrawer()}
                            >
                                Edit Shifts
                            </Button>
                        </Col>
                    </Row>
                }
                open={visible}
                footer=""
                onCancel={closeModel}
                destroyOnClose={true}
                bodyStyle={{
                    overflowY: "auto",
                    maxHeight: "calc(100vh - 200px)",
                }}
            >
                <Form
                    form={form}
                    name="basic"
                    labelCol={{
                        span: 16,
                    }}
                    wrapperCol={{
                        span: 16,
                    }}
                    onFinish={handleSubmit}
                >
                    <Form.Item label="ID" name="id" className="hidden">
                        <Input
                            id="id"
                            className="upcoming-form-inputs"
                            disabled
                        />
                    </Form.Item>
                    <Form.Item name="shift_timings" className="hidden">
                        <Input id="shift_timings" disabled />
                    </Form.Item>
                    <Form.Item name="shift_settings" className="hidden">
                        <Input id="shift_settings" disabled />
                    </Form.Item>
                    <Form.Item
                        label="Property Name"
                        name="property_name"
                        rules={[
                            {
                                required: true,
                                message: "Please enter Property Name!",
                            },
                        ]}
                    >
                        <Input placeholder="Property Name" id="property_name" />
                    </Form.Item>
                    <Form.Item
                        label="Address"
                        name="property_address"
                        rules={[
                            {
                                required: true,
                                message: "Please enter Address!",
                            },
                        ]}
                    >
                        <Input placeholder="Address" id="property_address" />
                    </Form.Item>
                    <Form.Item
                        label="City"
                        name="property_city"
                        rules={[
                            {
                                required: true,
                                message: "Please enter City!",
                            },
                        ]}
                    >
                        <Input placeholder="City" id="property_city" />
                    </Form.Item>
                    <Form.Item
                        label="State"
                        name="property_state"
                        rules={[
                            {
                                required: true,
                                message: "Please enter State!",
                            },
                        ]}
                    >
                        <Input placeholder="State" id="property_state" />
                    </Form.Item>
                    <Form.Item
                        label="Zipcode"
                        name="property_zip_code"
                        rules={[
                            {
                                required: true,
                                message: "Please enter Zipcode!",
                            },
                        ]}
                    >
                        <Input placeholder="Zipcode" id="property_zip_code" />
                    </Form.Item>
                    <Form.Item
                        label="Brand"
                        name="brand"
                        rules={[
                            {
                                required: true,
                                message: "Please enter Brand!",
                            },
                        ]}
                    >
                        <Input placeholder="Brand" id="brand" />
                    </Form.Item>
                    <Form.Item
                        label="Ship Date"
                        name="ship_date"
                        valuePropName="defaultValue"
                    >
                        <DatePicker
                            allowClear={false}
                            format={"MM-DD-YYYY"}
                            placeholder="Ship Date"
                            id="ship_date"
                        />
                    </Form.Item>
                    <Form.Item
                        label="Live Date"
                        name="live_date"
                        valuePropName="defaultValue"
                    >
                        <DatePicker
                            allowClear={false}
                            format={"MM-DD-YYYY"}
                            placeholder="Live Date"
                            id="live_date"
                        />
                    </Form.Item>
                    <Form.Item label="Lock Provider" name="lock_provider">
                        <Input placeholder="Lock Provider" id="lock_provider" />
                    </Form.Item>
                    <Form.Item label="Property Key" name="property_key">
                        <Input placeholder="Property Key" id="property_key" />
                    </Form.Item>
                    <Form.Item
                        label="Property Key Software"
                        name="property_key_software"
                    >
                        <Input
                            placeholder="Property Key Software"
                            id="property_key_software"
                        />
                    </Form.Item>
                    <Form.Item label="PBX" name="pbx">
                        <Input placeholder="PBX" id="pbx" />
                    </Form.Item>
                    <Form.Item label="PMS" name="pms">
                        <Input placeholder="PMS" id="pms" />
                    </Form.Item>
                    <Form.Item label="Kiosk Generation" name="gen_kiosk">
                        <Input placeholder="Kiosk Generation" id="gen_kiosk" />
                    </Form.Item>
                    <Form.Item label="Number of Kiosk" name="number_of_kiosk">
                        <InputNumber id="number_of_kiosk" min={0} />
                    </Form.Item>
                    <Form.Item
                        label="Number of Agents"
                        name="kiosk_frontdesk_agents"
                    >
                        <InputNumber id="kiosk_frontdesk_agents" min={0} />
                    </Form.Item>
                    <Form.Item label="Credit Terminal" name="credit_terminal">
                        <Input
                            placeholder="Credit Terminal"
                            id="credit_terminal"
                        />
                    </Form.Item>
                    {/* <Form.Item label="Shifts" name="shifts">
                        <InputNumber id="shifts" min={0} />
                    </Form.Item> */}
                    <Form.Item
                        label="Shift Settings"
                        name="shift_date_settings"
                    >
                        {weekDays.map((e) => (
                            <>
                                <Checkbox
                                    key={e.key}
                                    onChange={setShiftDaysCheckbox}
                                    value={e.key}
                                    checked={shiftDays.includes(e.key)}
                                >
                                    {e.title}
                                </Checkbox>
                                <DateWiseshift
                                    weekDay={e.key}
                                    selectedShift={form.getFieldValue(
                                        "shift_settings",
                                    )}
                                />
                            </>
                        ))}
                    </Form.Item>
                    <Form.Item label="Kiosk Serial" name="kiosk_serial">
                        <Input placeholder="Kiosk Serial" id="kiosk_serial" />
                    </Form.Item>
                    <Form.Item label="Internet Service" name="internet_service">
                        <Input
                            placeholder="Internet Service"
                            id="internet_service"
                        />
                    </Form.Item>
                    <Form.Item label="Notes" name="notes">
                        <Input placeholder="Notes" id="notes" />
                    </Form.Item>
                    <Form.Item label="Kiosk Id" name="kiosk_id">
                        <Input placeholder="Kiosk Id" id="kiosk_id" />
                    </Form.Item>
                    <Form.Item label="HIM Version" name="him_version">
                        <Input placeholder="HIM Version" id="him_version" />
                    </Form.Item>
                    <Form.Item label="TTI Version" name="tti_version">
                        <Input placeholder="TTI Version" id="tti_version" />
                    </Form.Item>
                    <Form.Item label="Kiosk Version" name="kiosk_version">
                        <Input
                            placeholder="Kiosk Version"
                            id="kiosk_version"
                            disabled
                        />
                    </Form.Item>
                    <Form.Item label="Desktop Name" name="desktop_name">
                        <Input placeholder="Desktop Name" id="desktop_name" />
                    </Form.Item>
                    <Form.Item label="Anydesk" name="anydesk">
                        <Input placeholder="Anydesk" id="anydesk" />
                    </Form.Item>
                    <Form.Item label="IP Address" name="ip_address">
                        <Input placeholder="IP Address" id="ip_address" />
                    </Form.Item>
                    <Form.Item label="PBX Contact" name="pbx_contact">
                        <Input placeholder="PBX Contact" id="pbx_contact" />
                    </Form.Item>
                    <Form.Item label="Lock Contact" name="lock_contact">
                        <Input placeholder="Lock Contact" id="lock_contact" />
                    </Form.Item>
                    <Form.Item label="PMS Contact" name="pms_contact">
                        <Input placeholder="PMS Contact" id="pms_contact" />
                    </Form.Item>
                    <Form.Item label="PMS URL" name="pms_url">
                        <Input placeholder="PMS URL" id="pms_url" />
                    </Form.Item>
                    <Form.Item
                        label="Aavgo support user"
                        name="support_user"
                        style={{ wordBreak: "break-all" }}
                    >
                        <Input
                            placeholder="Aavgo support user"
                            id="support_user"
                        />
                    </Form.Item>
                    <Form.Item label="Hotel ID" name="hotel_id">
                        <Input placeholder="Hotel ID" id="hotel_id" />
                    </Form.Item>
                    <Form.Item label="Kiosk Number" name="kiosk_number">
                        <Input placeholder="Kiosk Number" id="kiosk_number" />
                    </Form.Item>
                    <Form.Item
                        label="Aavgo Module Service Version Number"
                        name="module_service_version_number"
                    >
                        <Input
                            placeholder="Aavgo Module Service Version Number"
                            id="module_service_version_number"
                        />
                    </Form.Item>
                    <Form.Item
                        label="Aavgo Service Health Monitor Version Number"
                        name="health_monitor_version_number"
                    >
                        <Input
                            placeholder="Aavgo Service Health Monitor Version Number"
                            id="health_monitor_version_number"
                        />
                    </Form.Item>
                    <Form.Item
                        label="Aavgo FTP Service Version Number"
                        name="ftp_service_version_number"
                    >
                        <Input
                            placeholder="Aavgo FTP Service Version Number"
                            id="ftp_service_version_number"
                        />
                    </Form.Item>
                    <Form.Item
                        label="Bill Acceptor Port Number"
                        name="bill_acceptor_port_number"
                    >
                        <Input
                            placeholder="Bill Acceptor Port Number"
                            id="bill_acceptor_port_number"
                        />
                    </Form.Item>
                    <Form.Item
                        label="Bill Dispenser Port Number"
                        name="bill_dispenser_port_number"
                    >
                        <Input
                            placeholder="Bill Dispenser Port Number"
                            id="bill_dispenser_port_number"
                        />
                    </Form.Item>
                    <Form.Item
                        label="Printer Port Number"
                        name="printer_port_number"
                    >
                        <Input
                            placeholder="Printer Port Number"
                            id="printer_port_number"
                        />
                    </Form.Item>
                    <Form.Item
                        label="Key Dispenser Port Number"
                        name="key_dispenser_port_number"
                    >
                        <Input
                            placeholder="Key Dispenser Port Number"
                            id="key_dispenser_port_number"
                        />
                    </Form.Item>
                    <Form.Item label="PMS Settings">
                        {getUserNamePasswordFormList("pms_settings", userRole)}
                    </Form.Item>
                    <Form.Item label="Lock Settings">
                        {getUserNamePasswordFormList("lock_settings", userRole)}
                    </Form.Item>
                    <Form.Item label="General Settings">
                        {getUserNamePasswordFormList(
                            "general_settings",
                            userRole,
                        )}
                    </Form.Item>
                    <Form.Item label="Confluence URL" name="confluence_url">
                        <Input
                            placeholder="Confluence URL"
                            id="confluence_url"
                        />
                    </Form.Item>
                    <Form.Item
                        label="Kiosk Property Notes"
                        name="kiosk_property_notes"
                    >
                        <Input.TextArea
                            rows={4}
                            maxLength={500}
                            placeholder="Kiosk Property Notes"
                            id="kiosk_property_notes"
                        />
                    </Form.Item>
                    <Form.Item
                        wrapperCol={{
                            offset: 8,
                            span: 16,
                        }}
                    >
                        <Button type="primary" htmlType="submit">
                            Submit
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
            {/* Show Kiosk properties history */}
            <Modal
                title="History"
                open={historyTableVisible}
                footer=""
                onCancel={closeHistoryModel}
                destroyOnClose={true}
            >
                <CustomTable
                    columns={historyColumns}
                    tableData={historyTableData}
                    noOfrecords={totalHistoryRecords}
                    handleTableChange={handleHistoryTableChange}
                    rowKey="id"
                    className="kiosk-property-table"
                    isScroll
                ></CustomTable>
            </Modal>
            <Modal
                title={"Authentication"}
                open={authenticationModalVisible}
                onCancel={closeAuthenticationModal}
                destroyOnClose={true}
                footer=""
            >
                <Form
                    form={authenticationForm}
                    name="basic"
                    labelCol={{
                        span: 8,
                    }}
                    wrapperCol={{
                        span: 16,
                    }}
                    onFinish={handleAuthenticationSubmit}
                    autoComplete="off"
                >
                    <Form.Item
                        label="Username"
                        name="email"
                        rules={[
                            {
                                required: true,
                                message: "Please input user name!",
                            },
                        ]}
                    >
                        <Input
                            placeholder="user"
                            id="email"
                            className="upcoming-form-inputs"
                        ></Input>
                    </Form.Item>

                    <Form.Item
                        label="Password"
                        name="password"
                        rules={[
                            {
                                required: true,
                                message: "Please enter password!",
                            },
                        ]}
                    >
                        <Input.Password
                            type="password"
                            placeholder="Password"
                            id="password"
                            className="upcoming-form-inputs"
                        ></Input.Password>
                    </Form.Item>

                    <Form.Item name="field" className="hidden">
                        <Input id="field"></Input>
                    </Form.Item>
                    <Form.Item name="property_id" className="hidden">
                        <Input id="property_id"></Input>
                    </Form.Item>
                    <Form.Item
                        wrapperCol={{
                            offset: 8,
                            span: 16,
                        }}
                    >
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={buttonStatus}
                        >
                            Submit
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
            {/* Import kiosk properties */}
            <Modal
                width={"95%"}
                title="Confirm Add"
                open={importModalVisible}
                footer={
                    <Button type="primary" onClick={confirmImport}>
                        Confirm
                    </Button>
                }
                onCancel={closeImportModel}
                destroyOnClose={true}
            >
                <CustomTable
                    columns={importColumns}
                    tableData={importData}
                    // noOfrecords={totalHistoryRecords}
                    handleTableChange={() => {}}
                    rowKey="id"
                    className="kiosk-property-table"
                    isScroll
                ></CustomTable>
            </Modal>
            {/* Show kiosk properties  */}
            <Drawer
                destroyOnClose
                placement="right"
                title="View"
                open={viewVisible}
                onClose={closeViewModel}
            >
                <Collapse
                    style={{
                        width: "30vw",
                        margin: "20px",
                    }}
                    expandIconPosition="start"
                >
                    {viewColumns.map((k) => {
                        return (
                            <Panel header={k.Header} key={k.Header}>
                                {k.keys.map((value) => {
                                    let viewValue = viewData[value.key];

                                    if (value.title == "Shift Settings") {
                                        viewValue = weekDays.map((d, index) => {
                                            const temp = {};
                                            const shiftsWithTime = viewData[
                                                value.key
                                            ]?.[index]
                                                ? viewData[value.key]?.[
                                                      index
                                                  ].map((timings) => {
                                                      const shiftTime =
                                                          viewData.shift_timings.find(
                                                              (t) =>
                                                                  t.type ==
                                                                  timings,
                                                          );
                                                      const time = `${timings}${
                                                          shiftTime &&
                                                          `(${formatDate(
                                                              shiftTime.start_time,
                                                              "HH:mm",
                                                          )} - ${formatDate(
                                                              shiftTime.end_time,
                                                              "HH:mm",
                                                          )}) `
                                                      }`;
                                                      return time;
                                                  })
                                                : "NA";
                                            temp[d.title] = shiftsWithTime;
                                            return temp;
                                        });
                                    }
                                    return (
                                        <p
                                            key={value.title}
                                            className="margin-10"
                                        >
                                            <Text strong>{value.title} : </Text>
                                            <Text italic>
                                                {Array.isArray(viewValue)
                                                    ? viewValue.map((e) => {
                                                          return (
                                                              <div
                                                                  key={
                                                                      Object.keys(
                                                                          e,
                                                                      )[0]
                                                                  }
                                                                  style={{
                                                                      marginLeft:
                                                                          "50px",
                                                                  }}
                                                              >
                                                                  <Text strong>
                                                                      {`${
                                                                          Object.keys(
                                                                              e,
                                                                          )[0]
                                                                      } : `}
                                                                  </Text>
                                                                  <Text>
                                                                      {`${
                                                                          Object.values(
                                                                              e,
                                                                          )[0]
                                                                      }`}
                                                                  </Text>
                                                                  <br />
                                                              </div>
                                                          );
                                                      })
                                                    : viewValue}
                                            </Text>
                                            <br />
                                        </p>
                                    );
                                })}
                            </Panel>
                        );
                    })}
                </Collapse>
            </Drawer>
            {/* Edit hotel shift timings */}
            <Drawer
                destroyOnClose
                placement="right"
                title="Hotel Shifts"
                open={shiftVisible}
                onClose={closeShiftDrawer}
                width={"50%"}
            >
                <Form
                    onFinish={(values) => {
                        form.setFieldValue("shift_timings", values.timings);
                        setShiftTimings(values.timings);
                        closeShiftDrawer();
                    }}
                    initialValues={{
                        timings: shift_timings,
                    }}
                >
                    <Form.List name="timings">
                        {(fields, { add, remove }) => {
                            return (
                                <>
                                    {fields.map((field, index) => (
                                        <Form.Item
                                            key={field.key}
                                            label={index === 0 ? "Shifts" : ""}
                                            style={{
                                                margin: 0,
                                                justifyContent: "end",
                                            }}
                                            required
                                        >
                                            <Row>
                                                <Col xs={20}>
                                                    <Form.Item
                                                        name={[
                                                            field.name,
                                                            "type",
                                                        ]}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message:
                                                                    "Shift Type is required.",
                                                            },
                                                        ]}
                                                    >
                                                        <Input type="text" />
                                                    </Form.Item>
                                                    <Form.Item
                                                        name={[
                                                            field.name,
                                                            "start_time",
                                                        ]}
                                                        label="Start Time"
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message:
                                                                    "Start Time is required.",
                                                            },
                                                        ]}
                                                    >
                                                        <DatePicker
                                                            allowClear={false}
                                                            picker="time"
                                                            format={"HH:mm"}
                                                        />
                                                    </Form.Item>
                                                    <Form.Item
                                                        name={[
                                                            field.name,
                                                            "end_time",
                                                        ]}
                                                        label="End Time"
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message:
                                                                    "End Time is required.",
                                                            },
                                                        ]}
                                                    >
                                                        <DatePicker
                                                            allowClear={false}
                                                            picker="time"
                                                            format={"HH:mm"}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={4}>
                                                    {fields.length > 1 && (
                                                        <MinusCircleOutlined
                                                            className="dynamic-delete-button"
                                                            onClick={() =>
                                                                remove(
                                                                    field.name,
                                                                )
                                                            }
                                                        />
                                                    )}
                                                </Col>
                                            </Row>
                                        </Form.Item>
                                    ))}
                                    <Form.Item>
                                        <Button onClick={() => add()}>
                                            Add
                                        </Button>
                                    </Form.Item>
                                </>
                            );
                        }}
                    </Form.List>
                    <Form.Item
                        wrapperCol={{
                            offset: 8,
                            span: 16,
                        }}
                    >
                        <Button type="primary" htmlType="submit">
                            Submit
                        </Button>
                    </Form.Item>
                </Form>
            </Drawer>
            {addHotelForKioskId != 0 && (
                <AddHotel
                    kiosk_prop_id={addHotelForKioskId}
                    onClose={(refetch) => closeAddHotelForm(refetch)}
                />
            )}
        </>
    );
}

export { KioskPropertiesTable };
